import type { Column } from '../components/DataGrid'
import FileIconWithText from '../components/FileIconWithText'
import { formatUserDateTime } from './dateFormatting'

export interface UserDateTimePreferences {
  timeFormat24h?: boolean
  dateFormat?: string
  timeFormat?: string
  locale?: string
}

export const formatDateString = (date: Date, dateFormat: string): string => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const shortMonth = monthNames[date.getMonth()]

  const replacements: Record<string, string> = {
    yyyy: String(year),
    MMM: shortMonth,
    MM: month,
    dd: day
  }

  return dateFormat.replace(/yyyy|MMM|MM|dd/g, (token) => replacements[token] ?? token)
}

export const formatTimeString = (date: Date, timeFormat: string, is24h: boolean): string => {
  let hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  if (is24h) {
    const paddedHours = String(hours).padStart(2, '0')
    return `${paddedHours}${timeFormat}${minutes}${timeFormat}${seconds}`
  }

  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12 // Convert to 12-hour and handle 0 hour
  return `${hours}${timeFormat}${minutes}${timeFormat}${seconds} ${ampm}`
}

export const formatUserDateTimeWithPreferences = (
  value: string | number | Date,
  includeTime: boolean,
  userPreferences?: UserDateTimePreferences
): string => {
  if (!value) return '-'

  if (!userPreferences) {
    return formatUserDateTime(value, includeTime)
  }

  try {
    let date: Date

    if (typeof value === 'string') {
      date = new Date(value)
    } else if (typeof value === 'number') {
      date = new Date(value)
    } else {
      date = value
    }

    if (isNaN(date.getTime())) {
      return '-'
    }

    const dateFormat = userPreferences.dateFormat ?? 'MM/dd/yyyy'
    const formattedDate = formatDateString(date, dateFormat)

    if (includeTime) {
      const timeFormat = userPreferences.timeFormat ?? ':'
      const is24h = userPreferences.timeFormat24h ?? false
      const formattedTime = formatTimeString(date, timeFormat, is24h)
      return `${formattedDate} ${formattedTime}`
    }

    return formattedDate
  } catch (error) {
    console.error('Error formatting date with preferences:', error)
    return '-'
  }
}

interface FileWithFsi {
  sourceFsi?: { name?: string; type?: string }
  draftFsi?: { name?: string; type?: string }
  targetFsi?: { name?: string; type?: string }
}

interface ItemWithFiles {
  files?: FileWithFsi[]
  filesCount?: number
  status?: string
}

const getDocumentName = (item: ItemWithFiles): string => {
  if (!item.files || item.files.length === 0) {
    return '-'
  }

  if (item.filesCount && item.filesCount > 1) {
    return `${item.filesCount} documents`
  }

  const file = item.files[0]

  // Follow the same order as Angular task.service.js: sourceFsi -> draftFsi -> targetFsi
  if (file.sourceFsi?.name) {
    return file.sourceFsi.name
  }

  if (file.draftFsi?.name) {
    return file.draftFsi.name
  }

  if (file.targetFsi?.name) {
    return file.targetFsi.name
  }

  return '-'
}

const getFileType = (item: ItemWithFiles): 'DOCUMENT' | 'MULTIPLE' | 'FOLDER' => {
  if (!item.files || item.files.length === 0) {
    return 'DOCUMENT'
  }

  if (item.filesCount && item.filesCount > 1) {
    return 'MULTIPLE'
  }

  const file = item.files[0]

  // Check the same order as getDocumentName
  const fsi = file.sourceFsi ?? file.draftFsi ?? file.targetFsi

  if (fsi?.type === 'FOLDER') {
    return 'FOLDER'
  }

  // For all other file types, return 'DOCUMENT'
  // The FileIcon component will handle specific file type icons based on fileName extension
  return 'DOCUMENT'
}

const isItemInActiveWorkflow = (item: ItemWithFiles): boolean => {
  return item.status === 'ACTIVE'
}

interface DateColumnOptions<T> {
  headerLabel: string
  accessor: keyof T | ((item: T) => string | number | Date | null | undefined)
  sortable?: boolean
  sortKey?: string
  style?: React.CSSProperties
  useReportsFormat?: boolean
  userPreferences?: UserDateTimePreferences
}

export function createDateColumn<T>(options: DateColumnOptions<T>): Column<T> {
  const { headerLabel, accessor, sortable = true, sortKey, style, userPreferences } = options

  return {
    header: headerLabel,
    accessor: accessor as keyof T | ((item: T) => React.ReactNode),
    sortable,
    sortKey,
    style: style ?? { minWidth: '130px', width: '130px' },
    render: (value: unknown) => {
      if (!value) return '-'

      if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
        return formatUserDateTimeWithPreferences(value, false, userPreferences)
      }

      return '-'
    }
  }
}

export function createDateTimeColumn<T>(options: DateColumnOptions<T>): Column<T> {
  const { headerLabel, accessor, sortable = true, sortKey, style, userPreferences } = options

  return {
    header: headerLabel,
    accessor: accessor as keyof T | ((item: T) => React.ReactNode),
    sortable,
    sortKey,
    style: style ?? { minWidth: '130px', width: '130px' },
    render: (value: unknown) => {
      if (!value) return '-'
      if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
        return formatUserDateTimeWithPreferences(value, true, userPreferences)
      }

      return '-'
    }
  }
}

export const createDocumentNameColumn = <T extends ItemWithFiles>(t: (key: string) => string): Column<T> => {
  return {
    header: t('DOCUMENT_NAME'),
    accessor: (item: T) => getDocumentName(item),
    sortable: false,
    style: {
      paddingLeft: '42px' as const // Extra space for file icon, matching Angular
    },
    render: (value: unknown, item?: T) => {
      const displayValue = value as string

      // Handle empty/no document case
      if (displayValue === '-') {
        return (
          <div
            style={{
              marginLeft: '-30px', // Match Angular .no-filename styling
              paddingTop: '6px', // Center vertically in 36px row
              height: '100%'
            }}
          >
            {displayValue}
          </div>
        )
      }

      if (item?.filesCount && item.filesCount > 1) {
        return (
          <FileIconWithText
            fileName="Multiple Documents"
            fileType="MULTIPLE"
            filesCount={item.filesCount}
            isValid={true}
            isObsolete={false}
            showValidityIndicator={false}
            inWorkflow={isItemInActiveWorkflow(item)}
          />
        )
      }

      return (
        <FileIconWithText
          fileName={displayValue}
          fileType={item ? getFileType(item) : 'DOCUMENT'}
          isValid={true}
          isObsolete={false}
          showValidityIndicator={false}
          inWorkflow={item ? isItemInActiveWorkflow(item) : false}
        />
      )
    }
  }
}

/**
 * Creates a date formatter function with user preferences from auth context
 * @param auth - The auth service from context.service.auth
 * @param translate - The translate function from context.service.filter
 * @returns A function that formats dates according to user preferences
 */
export const createDateTimeFormatter = (
  auth:
    | { getUser?: () => { dateFormat?: string; timeFormat?: string; timeFormat24h?: boolean } | null | undefined }
    | undefined,
  translate: (key: string) => string
) => {
  const user = auth?.getUser?.()
  const userPreferences: UserDateTimePreferences = {
    dateFormat: user?.dateFormat,
    timeFormat: user?.timeFormat,
    timeFormat24h: user?.timeFormat24h
  }

  return (date: string | number | Date | null | undefined): string => {
    if (!date) return translate('NOT_AVAILABLE')
    return formatUserDateTimeWithPreferences(date, true, userPreferences)
  }
}

export type { ItemWithFiles, FileWithFsi, DateColumnOptions }
